import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Plus, Edit2, ToggleLeft, ToggleRight } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, Modal, ModalHeader, ModalBody, ModalFooter, Input, Textarea, EmptyState } from '../../../components/ui'

interface Category {
  id: number; name: string; slug: string; description: string | null
  is_active: boolean; display_order: number; posts_count: number
}

export default function BlogCategoriesPage() {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState({ name: '', description: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => { fetchCategories() }, [])

  async function fetchCategories() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/posts/categories'))
      setCategories(res.data.data)
    } catch { /* */ } finally { setLoading(false) }
  }

  function openCreate() {
    setEditingId(null)
    setForm({ name: '', description: '' })
    setErrors({})
    setModalOpen(true)
  }

  function openEdit(cat: Category) {
    setEditingId(cat.id)
    setForm({ name: cat.name, description: cat.description || '' })
    setErrors({})
    setModalOpen(true)
  }

  async function handleSave() {
    if (saving) return
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Nome é obrigatório'
    setErrors(e)
    if (Object.keys(e).length > 0) return

    setSaving(true)
    try {
      if (editingId) {
        await axios.put(buildApiUrl(`/v1/posts/categories/${editingId}`), form)
        toast('success', 'Categoria atualizada')
      } else {
        await axios.post(buildApiUrl('/v1/posts/categories'), form)
        toast('success', 'Categoria criada')
      }
      setModalOpen(false)
      fetchCategories()
    } catch (err: any) {
      toast('error', err.response?.data?.error || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  async function handleToggle(id: number) {
    await axios.put(buildApiUrl(`/v1/posts/categories/${id}/toggle`))
    fetchCategories()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/blog/posts')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Categorias</h1>
            <p className="text-gray-500 mt-1">Organize os posts por categoria</p>
          </div>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={openCreate}>Nova Categoria</Button>
      </div>

      {loading ? (
        <Card padding="none">
          <div className="p-5 space-y-3">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />)}</div>
        </Card>
      ) : categories.length === 0 ? (
        <Card>
          <EmptyState icon={<Plus className="w-7 h-7" />} title="Nenhuma categoria" description="Crie categorias para organizar os posts."
            action={<Button size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Criar</Button>} />
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Posts</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {categories.map(cat => (
                  <tr key={cat.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3.5 text-sm font-medium text-gray-900">{cat.name}</td>
                    <td className="px-4 py-3.5 text-xs text-gray-400 font-mono">/{cat.slug}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-500 max-w-[250px] truncate">{cat.description || '—'}</td>
                    <td className="px-4 py-3.5 text-center text-sm text-gray-700">{cat.posts_count}</td>
                    <td className="px-4 py-3.5 text-center">
                      <Badge variant={cat.is_active ? 'success' : 'gray'} size="sm" dot>{cat.is_active ? 'Ativa' : 'Inativa'}</Badge>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(cat)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleToggle(cat.id)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                          {cat.is_active ? <ToggleRight className="w-5 h-5 text-success-500" /> : <ToggleLeft className="w-5 h-5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setModalOpen(false)}>{editingId ? 'Editar Categoria' : 'Nova Categoria'}</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <Input label="Nome" value={form.name} onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setErrors(er => ({ ...er, name: '' })) }}
              placeholder="Ex: Notícias, Comunicados" error={errors.name} />
            <Textarea label="Descrição" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Descrição da categoria (opcional)" rows={2} />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} loading={saving}>{editingId ? 'Salvar' : 'Criar'}</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
